import type { SportsApiClient } from '@collector/sportsapi-client'
import type { Event, Participant } from '@collector/sportsapi-types'
import {
  useParticipants,
  useSubParticipants,
} from '@desktop/views/Relation/composables'
import { useEventId } from '@desktop/views/Relation/composables/useEventId'

import { keepWillNotPlaySubParticipants } from './probable-lineup-deferred-clear.utils'

type EventId = Event['id']
type ParticipantId = Participant['id']

const pendingParticipantIdsByEventId = new Map<EventId, Set<ParticipantId>>()

export function useProbableLineupDeferredClear(): {
  clearLineupsLocally: () => void
  isParticipantPendingDeferredClear: (participantId: ParticipantId) => boolean
  isParticipantPendingDeferredClearForEvent: (
    eventId: EventId,
    participantId: ParticipantId,
  ) => boolean
  resetDeferredClearState: () => void
  runDeferredClearLineupAfterSave: (
    sportsApiClient: SportsApiClient,
    participantId: ParticipantId,
  ) => Promise<void>
} {
  const eventId = useEventId()
  const {
    homeParticipant,
    awayParticipant,
    setHomeParticipant,
    setAwayParticipant,
  } = useParticipants()
  const {
    homeSubParticipants,
    awaySubParticipants,
    setHomeSubParticipants,
    setAwaySubParticipants,
  } = useSubParticipants()

  function isParticipantPendingDeferredClearForEvent(
    lookupEventId: EventId,
    participantId: ParticipantId,
  ): boolean {
    return (
      pendingParticipantIdsByEventId.get(lookupEventId)?.has(participantId) ??
      false
    )
  }

  function isParticipantPendingDeferredClear(
    participantId: ParticipantId,
  ): boolean {
    return isParticipantPendingDeferredClearForEvent(
      eventId.value,
      participantId,
    )
  }

  async function runDeferredClearLineupAfterSave(
    sportsApiClient: SportsApiClient,
    participantId: ParticipantId,
  ): Promise<void> {
    if (!isParticipantPendingDeferredClear(participantId)) return

    await sportsApiClient.Events.Participants.Lineups.clearLineup(
      eventId.value,
      participantId,
    )
    pendingParticipantIdsByEventId.get(eventId.value)?.delete(participantId)
  }

  function clearLineupsLocally(): void {
    const home = homeParticipant.value
    const away = awayParticipant.value
    if (!home || !away) return

    const pendingIds =
      pendingParticipantIdsByEventId.get(eventId.value) ??
      new Set<ParticipantId>()
    if (!pendingParticipantIdsByEventId.has(eventId.value))
      pendingParticipantIdsByEventId.set(eventId.value, pendingIds)

    for (const participantId of [home.id, away.id] as ParticipantId[])
      pendingIds.add(participantId)

    const homeWillNotPlay = keepWillNotPlaySubParticipants(
      homeSubParticipants.value,
    )
    const awayWillNotPlay = keepWillNotPlaySubParticipants(
      awaySubParticipants.value,
    )
    setHomeSubParticipants(homeWillNotPlay)
    setAwaySubParticipants(awayWillNotPlay)

    setHomeParticipant({ ...home, lineups_copied: false })
    setAwayParticipant({ ...away, lineups_copied: false })
  }

  function resetDeferredClearState(): void {
    pendingParticipantIdsByEventId.delete(eventId.value)
  }

  return {
    clearLineupsLocally,
    isParticipantPendingDeferredClear,
    isParticipantPendingDeferredClearForEvent,
    resetDeferredClearState,
    runDeferredClearLineupAfterSave,
  }
}
